import { Center, Grid, Pagination, Stack } from 'components'
import type { FC } from 'react'
import { memo } from 'react'
import type { ArcProject, PaginationMeta } from '../types'
import { ArcProjectCard } from './ArcProjectCard'
import { EmptyState } from './EmptyState'
import { ProjectCardSkeleton } from './ProjectCardSkeleton'

interface ProjectGridProps {
  projects: ArcProject[]
  isLoading: boolean
  isLibrary: boolean
  canEdit: (project: ArcProject) => boolean
  pagination: PaginationMeta | null
  onPageChange: (page: number) => void
  backlink?: string
}

const SKELETON_COUNT = 9

export const ProjectGrid: FC<ProjectGridProps> = memo(({
  projects,
  isLoading,
  isLibrary,
  canEdit,
  pagination,
  onPageChange,
  backlink,
}) => {
  if (isLoading) {
    return (
      <Grid>
        {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
          <Grid.Col key={`skeleton-${index}`} xs={12} sm={6} lg={4}>
            <ProjectCardSkeleton />
          </Grid.Col>
        ))}
      </Grid>
    )
  }

  if (projects.length === 0) {
    return <EmptyState isLibrary={isLibrary} />
  }

  const showPagination = pagination && pagination.pages > 1

  return (
    <Stack spacing="md">
      <Grid>
        {projects.map(project => (
          <Grid.Col key={project.id} xs={12} sm={6} lg={4}>
            <ArcProjectCard
              project={project}
              canEdit={canEdit(project)}
              isLibrary={isLibrary}
              backlink={backlink}
            />
          </Grid.Col>
        ))}
      </Grid>

      {showPagination && (
        <Center>
          <Pagination
            total={pagination.pages}
            value={pagination.page}
            onChange={onPageChange}
          />
        </Center>
      )}
    </Stack>
  )
})

ProjectGrid.displayName = 'ProjectGrid'
